import { Paginator } from 'array-paginator';
import { defineStore } from 'pinia';
import { ResolveListForExportCallback } from 'src/stores/_infrastructure/ResolveListForExportCallback';
import { completeListExportViewFor } from 'src/stores/_infrastructure/completeListExportView';
import { resolveDesktopModuleDateFilterForQuery } from 'src/stores/_infrastructure/dateTimeRangeQueryValue';
import { dedupByAggregateId } from 'src/stores/_infrastructure/dedupByAggregateId';
import {
	COMPLETE_LIST_EXPORT_LIMIT_HEADROOM,
	prepareQueryForCompleteListExport,
} from 'src/stores/_infrastructure/prepareQueryForCompleteListExport';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import { GetAllUsersWithAccessToTheModuleForTheUserFilterQuery } from 'src/stores/genericActivityList/queries';
import {
	TASK_LIST_GENERIC_ACTIVITY_LIST_SORT,
	TASK_LIST_GENERIC_ACTIVITY_LIST_VIEW_MODE_LARGE,
} from 'src/stores/taskList/localStorageKeys';
import {
	GetTheTaskModuleBoxesForTheUserQuery,
	GetTheTaskModuleTasksForTheUserQuery,
} from 'src/stores/taskList/queries';
import createTaskListState from 'src/stores/taskList/state';
import { TSMap } from 'typescript-map';

import { DateRangeSelection } from 'src/models/common';
import { FilterBoxes, FilterBoxesRunType, FilterUsers, FilterUsersRunType } from 'src/models/filter';
import {
	ActivityListTypeEnum,
	GenericActivityListPagination,
	GenericActivityListSort,
} from 'src/models/genericActivityList';
import { PreconfiguredTimeRanges } from 'src/models/miniCalendar';
import { ModuleNameEnum } from 'src/models/module/enums';
import { SerializedQuery } from 'src/models/query';
import { StatusEnum } from 'src/models/status/enums';
import { TaskListItem, TaskListItemRunType } from 'src/models/taskList';

import {
	removeScheduledTasksFromCollectionWithLogic,
	removeTheAggregateIdsFromTheCollection,
	setTheStateForTheAggregateIds,
} from 'src/util/aggregate/AggregateUtilityFunctions';
import { createCompareFunction } from 'src/util/collection/customCompareFunction';
import { DEFAULT_LIST_INSTANCE_ID, deriveListStoreId, isDefaultInstance } from 'src/util/listInstance/listInstanceKeys';

import {
	GetAllUsersWithAccessToTheModuleForTheUserFilterTaskListCallBack,
	GetTheAllTaskModuleTasksForTheUserCallBack,
	GetTheMineTaskModuleTasksForTheUserCallBack,
	GetTheOpenTaskModuleTasksForTheUserCallBack,
	GetTheTaskModuleBoxesForTheUserQueryCallBack,
} from './callbacks';
import {
	CompleteListExportFetchPayload,
	DeleteSelectedAggregateIdsPayload,
	ListFetchPayload,
	QueryStorePayload,
	RemoveScheduledTasksPayload,
	SetDateFilterPayload,
	SetDateTimeRangeFilterPayload,
	SetFilteredBoxesPayload,
	SetFilteredUsersPayload,
	SetListPayload,
	SetListStatePayload,
	SetPaginationPagePayload,
	SetPaginationPayload,
	SetSelectedSharedQueryPayload,
	SetSortPayload,
} from './interfaces';

const DEFAULT_ROWS_PER_PAGE = 100;
const DEFAULT_LIST_SORT: GenericActivityListSort = {
	direction: 'DESC',
	property: 'timestamp',
};

const createDefaultPaginationEntry = (): GenericActivityListPagination => ({
	page: 1,
	rowsPerPage: DEFAULT_ROWS_PER_PAGE,
});

const createDefaultPaginationMap = (): TSMap<ActivityListTypeEnum, GenericActivityListPagination> =>
	new TSMap<ActivityListTypeEnum, GenericActivityListPagination>([
		[ActivityListTypeEnum.ALL, createDefaultPaginationEntry()],
		[ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE, createDefaultPaginationEntry()],
		[ActivityListTypeEnum.MINE, createDefaultPaginationEntry()],
		[ActivityListTypeEnum.OPEN, createDefaultPaginationEntry()],
	]);

const replaceActiveQueryStoreSubscription = <T>({
	matchesLogicalView,
	nextSerializedQuery,
	parseQuery,
	queryStore,
	subscriptions,
}: {
	matchesLogicalView: (serializedQuery: SerializedQuery) => boolean;
	nextSerializedQuery: SerializedQuery;
	parseQuery: (serializedQuery: SerializedQuery) => T;
	queryStore: ListFetchPayload['queryStore'];
	subscriptions: SerializedQuery[];
}): SerializedQuery[] => {
	const staleSubscriptions = subscriptions.filter(matchesLogicalView);

	staleSubscriptions.forEach((serializedQuery) => {
		void queryStore?.unsubscribeQuery(parseQuery(serializedQuery));
	});

	return [...subscriptions.filter((serializedQuery) => !matchesLogicalView(serializedQuery)), nextSerializedQuery];
};

// Task 3.1e: Instanz-scoped Subscription-Opt-out. Die DEFAULT-Liste (`$id === 'taskList'`) bleibt bei aktiver
// Suche bewusst live-abonniert — klassisches Verhalten aus a1b8968ac für die Kanban-Multi-Lane-Suche. Nur
// isolierte Instanz-Stores (gespeicherte-Listen-Tabs) schalten die such-scoped Query stumm, sonst überschreibt
// der Subscription-Replay die view-scoped Antworten über alle drei Slices (Zähler-Bug 8/8/8). Leere Suche = No-op.
const applyInstanceSearchSubscriptionOptOut = (
	storeId: string,
	query: GetTheTaskModuleTasksForTheUserQuery,
	payload: ListFetchPayload
): void => {
	if (storeId === 'taskList') {
		return;
	}

	query.disableSubscriptionsForInstanceSearch(payload.searchTerm, payload.hiddenSearchTerm);
};

// Task 3.1f: isolierte gespeicherte-Listen-Tabs (`$id !== 'taskList'`) sollen view-scoped Zähler/Zeilen zeigen —
// deshalb unterdrücken ihre Query-Konstruktionen die Box→Custom-Kopplung (sonst kollabieren Offen/Meine/Alle auf
// dieselbe Custom-Query → 8/8/8). Der Default-Store behält die klassische Kopplung (byte-identisch). Wird an ALLE
// Konstruktionsstellen (Dispatch + Register + Export) gereicht, damit Subscription-Identifier konsistent bleiben.
const suppressBoxCustomCouplingForInstance = (storeId: string): boolean => storeId !== 'taskList';

const defineTaskListStore = (id: string) =>
	defineStore(id, {
		actions: {
			addQueryStoreSubscription(serializedQuery: SerializedQuery): void {
				this.queryStoreSubscriptions.push(serializedQuery);
			},
			clearQueryStoreSubscriptions(): void {
				this.queryStoreSubscriptions = [];
			},
			clearSubscriptions(payload: QueryStorePayload): void {
				if (payload.queryStore !== undefined) {
					this.queryStoreSubscriptions.forEach((serializedQuery: SerializedQuery) => {
						switch (serializedQuery.type) {
							case 'GetTheTaskModuleTasksForTheUserQuery':
								void payload.queryStore?.unsubscribeQuery(
									GetTheTaskModuleTasksForTheUserQuery.fromJSON<GetTheTaskModuleTasksForTheUserQuery>(
										serializedQuery.query
									)
								);
								break;
							default:
								break;
						}
					});
				}

				this.clearQueryStoreSubscriptions();
			},
			deleteSelectedAggregateIds(payload: DeleteSelectedAggregateIdsPayload): void {
				const repaginateAndSet = (
					taskList: TaskListItem[],
					totalCount: number,
					applyPage: (pagePayload: SetListPayload) => void
				): void => {
					const taskListCopy = removeTheAggregateIdsFromTheCollection(
						taskList as unknown as never[],
						payload.aggregateIds
					) as unknown as TaskListItem[];

					if (taskListCopy !== taskList) {
						const taskListCopyPaginator = new Paginator(taskListCopy, DEFAULT_ROWS_PER_PAGE, 1);

						while (taskListCopyPaginator.current <= taskListCopyPaginator.total) {
							applyPage({
								page: taskListCopyPaginator.current,
								payload: taskListCopyPaginator.page(taskListCopyPaginator.current) as TaskListItem[],
								totalCount: totalCount,
							});

							taskListCopyPaginator.next();
						}
					}
				};

				repaginateAndSet(this.taskListOpen, this.taskListOpenTotalCount, (listPayload) =>
					this.setOpenTaskList(listPayload)
				);
				repaginateAndSet(this.taskListAll, this.taskListAllTotalCount, (listPayload) =>
					this.setAllTaskList(listPayload)
				);
				repaginateAndSet(this.taskListMine, this.taskListMineTotalCount, (listPayload) =>
					this.setMineTaskList(listPayload)
				);
			},
			async fetchAllTaskList(payload: ListFetchPayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setAllTaskList({
						page: 1,
						payload: [],
						totalCount: 0,
					});
					return;
				}

				this.registerUnsubscribeFetchAllTaskList(payload);
				const fetchToken = useGenericActivityListStore().beginListFetch(this.$id, ActivityListTypeEnum.ALL);

				const sort = this.sortFor(ActivityListTypeEnum.ALL);
				const pagination = this.paginationFor(ActivityListTypeEnum.ALL);
				const page = pagination.page;
				const query = new GetTheTaskModuleTasksForTheUserQuery(
					'all',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					pagination,
					sort,
					suppressBoxCustomCouplingForInstance(this.$id)
				);
				applyInstanceSearchSubscriptionOptOut(this.$id, query, payload);
				const queryIdentifier = query.uniqueQueryIdentifier();

				this.setPaginationPage({ activityListType: ActivityListTypeEnum.ALL, page });
				this.setActiveQueryIdentifier(ActivityListTypeEnum.ALL, queryIdentifier);

				const callback = new GetTheAllTaskModuleTasksForTheUserCallBack(this, page, queryIdentifier);
				callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
				await payload.queryStore.query<TaskListItem[]>({
					callback,
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: TaskListItemRunType,
				});
			},
			async fetchFilterBoxes(payload: QueryStorePayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setFilterBoxes([]);
					return;
				}

				await payload.queryStore.query<Array<FilterBoxes>>({
					callback: new GetTheTaskModuleBoxesForTheUserQueryCallBack(this),
					createTime: new Date(),
					query: new GetTheTaskModuleBoxesForTheUserQuery(),
					responseType: 'json',
					runType: FilterBoxesRunType,
				});
			},
			async fetchFilterUsers(payload: QueryStorePayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setFilterUsers([]);
					return;
				}

				await payload.queryStore.query<Array<FilterUsers>>({
					callback: new GetAllUsersWithAccessToTheModuleForTheUserFilterTaskListCallBack(this),
					createTime: new Date(),
					query: new GetAllUsersWithAccessToTheModuleForTheUserFilterQuery(ModuleNameEnum.MODULE_TASK),
					responseType: 'json',
					runType: FilterUsersRunType,
				});
			},
			async fetchMineTaskList(payload: ListFetchPayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setMineTaskList({
						page: 1,
						payload: [],
						totalCount: 0,
					});
					return;
				}

				this.registerUnsubscribeFetchMineTaskList(payload);
				const fetchToken = useGenericActivityListStore().beginListFetch(this.$id, ActivityListTypeEnum.MINE);

				const sort = this.sortFor(ActivityListTypeEnum.MINE);
				const pagination = this.paginationFor(ActivityListTypeEnum.MINE);
				const page = pagination.page;
				const query = new GetTheTaskModuleTasksForTheUserQuery(
					'mine',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					pagination,
					sort,
					suppressBoxCustomCouplingForInstance(this.$id)
				);
				applyInstanceSearchSubscriptionOptOut(this.$id, query, payload);
				const queryIdentifier = query.uniqueQueryIdentifier();

				this.setPaginationPage({ activityListType: ActivityListTypeEnum.MINE, page });
				this.setActiveQueryIdentifier(ActivityListTypeEnum.MINE, queryIdentifier);

				const callback = new GetTheMineTaskModuleTasksForTheUserCallBack(this, page, queryIdentifier);
				callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
				await payload.queryStore.query<TaskListItem[]>({
					callback,
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: TaskListItemRunType,
				});
			},
			async fetchOpenTaskList(payload: ListFetchPayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setOpenTaskList({
						page: 1,
						payload: [],
						totalCount: 0,
					});
					return;
				}

				this.registerUnsubscribeFetchOpenTaskList(payload);
				const fetchToken = useGenericActivityListStore().beginListFetch(this.$id, ActivityListTypeEnum.OPEN);

				const sort = this.sortFor(ActivityListTypeEnum.OPEN);
				const pagination = this.paginationFor(ActivityListTypeEnum.OPEN);
				const page = pagination.page;
				const query = new GetTheTaskModuleTasksForTheUserQuery(
					'open',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					pagination,
					sort,
					suppressBoxCustomCouplingForInstance(this.$id)
				);
				applyInstanceSearchSubscriptionOptOut(this.$id, query, payload);
				const queryIdentifier = query.uniqueQueryIdentifier();

				this.setPaginationPage({ activityListType: ActivityListTypeEnum.OPEN, page });
				this.setActiveQueryIdentifier(ActivityListTypeEnum.OPEN, queryIdentifier);

				const callback = new GetTheOpenTaskModuleTasksForTheUserCallBack(this, page, queryIdentifier);
				callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
				await payload.queryStore.query<TaskListItem[]>({
					callback,
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: TaskListItemRunType,
				});
			},
			async fetchTheCompleteListForExport(
				payload: CompleteListExportFetchPayload
			): Promise<TaskListItem[] | null> {
				if (payload.queryStore === undefined) {
					return null;
				}

				const totalCountByListType: Partial<Record<ActivityListTypeEnum, number>> = {
					[ActivityListTypeEnum.ALL]: this.taskListAllTotalCount,
					[ActivityListTypeEnum.MINE]: this.taskListMineTotalCount,
					[ActivityListTypeEnum.OPEN]: this.taskListOpenTotalCount,
				};
				const pagination: GenericActivityListPagination = {
					page: 1,
					rowsPerPage:
						(totalCountByListType[payload.activityListType] ?? 0) + COMPLETE_LIST_EXPORT_LIMIT_HEADROOM,
				};
				const query = prepareQueryForCompleteListExport(
					new GetTheTaskModuleTasksForTheUserQuery(
						completeListExportViewFor(payload.activityListType),
						resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
						this.filteredBoxIds,
						this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
						payload.searchTerm,
						payload.hiddenSearchTerm,
						pagination,
						this.sortFor(payload.activityListType),
						suppressBoxCustomCouplingForInstance(this.$id)
					)
				);

				let completeList: TaskListItem[] | null = null;

				await payload.queryStore.query<TaskListItem[]>({
					callback: new ResolveListForExportCallback<TaskListItem>((list) => {
						completeList = list;
					}),
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: TaskListItemRunType,
				});

				return completeList;
			},
			isLatestQueryIdentifier(activityListType: ActivityListTypeEnum, queryIdentifier: string): boolean {
				return (this.activeQueryIdentifierByListType.get(activityListType) ?? '') === queryIdentifier;
			},
			registerUnsubscribeFetchAllTaskList(payload: ListFetchPayload): void {
				if (payload.queryStore === undefined) {
					return;
				}

				const pagination = this.paginationFor(ActivityListTypeEnum.ALL);
				const query = new GetTheTaskModuleTasksForTheUserQuery(
					'all',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					pagination,
					this.sortFor(ActivityListTypeEnum.ALL),
					suppressBoxCustomCouplingForInstance(this.$id)
				);

				this.replaceTaskListQueryStoreSubscription({
					query,
					queryStore: payload.queryStore,
				});
			},
			registerUnsubscribeFetchMineTaskList(payload: ListFetchPayload): void {
				if (payload.queryStore === undefined) {
					return;
				}

				const pagination = this.paginationFor(ActivityListTypeEnum.MINE);
				const query = new GetTheTaskModuleTasksForTheUserQuery(
					'mine',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					pagination,
					this.sortFor(ActivityListTypeEnum.MINE),
					suppressBoxCustomCouplingForInstance(this.$id)
				);

				this.replaceTaskListQueryStoreSubscription({
					query,
					queryStore: payload.queryStore,
				});
			},
			registerUnsubscribeFetchOpenTaskList(payload: ListFetchPayload): void {
				if (payload.queryStore === undefined) {
					return;
				}

				const pagination = this.paginationFor(ActivityListTypeEnum.OPEN);
				const query = new GetTheTaskModuleTasksForTheUserQuery(
					'open',
					resolveDesktopModuleDateFilterForQuery(this.dateFilter, this.dateTimeRangeFilter),
					this.filteredBoxIds,
					this.filteredUsers.map((filteredUser: FilterUsers) => filteredUser.userId),
					payload.searchTerm,
					payload.hiddenSearchTerm,
					pagination,
					this.sortFor(ActivityListTypeEnum.OPEN),
					suppressBoxCustomCouplingForInstance(this.$id)
				);

				this.replaceTaskListQueryStoreSubscription({
					query,
					queryStore: payload.queryStore,
				});
			},
			removeScheduledTasksFromLists(payload: RemoveScheduledTasksPayload): void {
				const repaginateAndSetIfChanged = (
					taskList: TaskListItem[],
					totalCount: number,
					applyPage: (pagePayload: SetListPayload) => void
				): void => {
					const taskListCopy = removeScheduledTasksFromCollectionWithLogic(
						taskList as unknown as never[],
						payload.aggregateIds,
						payload.targetDate,
						'other'
					) as unknown as TaskListItem[];

					if (taskListCopy.length !== taskList.length) {
						const taskListCopyPaginator = new Paginator(taskListCopy, DEFAULT_ROWS_PER_PAGE, 1);

						while (taskListCopyPaginator.current <= taskListCopyPaginator.total) {
							applyPage({
								page: taskListCopyPaginator.current,
								payload: taskListCopyPaginator.page(taskListCopyPaginator.current) as TaskListItem[],
								totalCount: totalCount,
							});

							taskListCopyPaginator.next();
						}
					}
				};

				repaginateAndSetIfChanged(this.taskListMine, this.taskListMineTotalCount, (listPayload) =>
					this.setMineTaskList(listPayload)
				);
				repaginateAndSetIfChanged(this.taskListAll, this.taskListAllTotalCount, (listPayload) =>
					this.setAllTaskList(listPayload)
				);
			},
			replaceTaskListQueryStoreSubscription(payload: {
				query: GetTheTaskModuleTasksForTheUserQuery;
				queryStore: ListFetchPayload['queryStore'];
			}): void {
				const nextSerializedQuery: SerializedQuery = {
					query: JSON.stringify(payload.query),
					type: 'GetTheTaskModuleTasksForTheUserQuery',
				};
				const expectedStandardFilterSelection = payload.query.standardFilterSelection;

				this.queryStoreSubscriptions = replaceActiveQueryStoreSubscription({
					matchesLogicalView: (serializedQuery) => {
						if (serializedQuery.type !== nextSerializedQuery.type) {
							return false;
						}

						return (
							GetTheTaskModuleTasksForTheUserQuery.fromJSON<GetTheTaskModuleTasksForTheUserQuery>(
								serializedQuery.query
							).standardFilterSelection === expectedStandardFilterSelection
						);
					},
					nextSerializedQuery,
					parseQuery: (serializedQuery) =>
						GetTheTaskModuleTasksForTheUserQuery.fromJSON<GetTheTaskModuleTasksForTheUserQuery>(
							serializedQuery.query
						),
					queryStore: payload.queryStore,
					subscriptions: this.queryStoreSubscriptions,
				});
			},
			reset(payload: QueryStorePayload): void {
				this.clearSubscriptions({
					queryStore: payload.queryStore,
				});
				this.resetState();
			},
			resetState(): void {
				const today = new Date().toISOString().split('T')[0];
				this.activeQueryIdentifierByListType = new TSMap<ActivityListTypeEnum, string>([
					[ActivityListTypeEnum.ALL, ''],
					[ActivityListTypeEnum.MINE, ''],
					[ActivityListTypeEnum.OPEN, ''],
				]);
				this.dateFilter = today;
				this.today = today;
				this.dateTimeRangeFilter = PreconfiguredTimeRanges.TODAY;
				this.filteredUsers = [];
				this.filteredBoxIds = [];
				this.selectedSharedQueryId = '';
				this.queryStoreSubscriptions = [];
				this.taskListOpenTotalCount = 0;
				this.taskListOpen = [];
				this.taskListOpenSliced.clear();
				this.taskListAllTotalCount = 0;
				this.taskListAll = [];
				this.taskListAllSliced.clear();
				this.taskListMineTotalCount = 0;
				this.taskListMine = [];
				this.taskListMineSliced.clear();
				this.pagination = createDefaultPaginationMap();
			},
			setActiveQueryIdentifier(activityListType: ActivityListTypeEnum, queryIdentifier: string): void {
				this.activeQueryIdentifierByListType.set(activityListType, queryIdentifier);
			},
			setAllTaskList(payload: SetListPayload): void {
				this.taskListAllTotalCount = payload.totalCount;
				this.taskListAllSliced.set(payload.page, payload.payload);

				const sort = this.sortFor(ActivityListTypeEnum.ALL);
				this.taskListAll = dedupByAggregateId(
					[...this.taskListAllSliced.values()].flatMap((entry) => entry)
				).sort(createCompareFunction<TaskListItem>(sort.property as keyof TaskListItem, sort.direction));
			},
			setDateFilterAction(payload: SetDateFilterPayload): void {
				this.setDateFilterValue(payload.dateFilter);
			},
			setDateFilterValue(payload: DateRangeSelection | string): void {
				this.dateFilter = payload || '';
			},
			setDateTimeRangeFilter(payload: SetDateTimeRangeFilterPayload): void {
				this.setDateTimeRangeFilterValue(payload.dateTimeRangeFilter);
			},
			setDateTimeRangeFilterValue(payload: PreconfiguredTimeRanges): void {
				this.dateTimeRangeFilter = payload;
			},
			setFilterBoxes(payload: Array<FilterBoxes>): void {
				this.filterBoxes = payload || [];
			},
			setFilterUsers(payload: Array<FilterUsers>): void {
				this.filterUsers = payload || [];
			},
			setFilteredBoxes(payload: Array<string>): void {
				this.filteredBoxIds = payload || [];
			},
			setFilteredBoxesAction(payload: SetFilteredBoxesPayload): void {
				this.setFilteredBoxes(payload.boxIdFilter);
			},
			setFilteredUsers(payload: Array<FilterUsers>): void {
				this.filteredUsers = payload || [];
			},
			setFilteredUsersAction(payload: SetFilteredUsersPayload): void {
				this.setFilteredUsers(payload.filterUsers);
			},
			setListLoadError(): void {
				const genericActivityListStore = useGenericActivityListStore();
				genericActivityListStore.setListLoadError();
			},
			setListViewModeLarge(payload: boolean): void {
				if (this.$id === 'taskList') {
					localStorage.setItem(TASK_LIST_GENERIC_ACTIVITY_LIST_VIEW_MODE_LARGE, JSON.stringify(payload));
				}
				this.listViewModeLarge = payload;
			},
			setMineTaskList(payload: SetListPayload): void {
				this.taskListMineTotalCount = payload.totalCount;
				this.taskListMineSliced.set(payload.page, payload.payload);

				const sort = this.sortFor(ActivityListTypeEnum.MINE);
				this.taskListMine = dedupByAggregateId(
					[...this.taskListMineSliced.values()].flatMap((entry) => entry)
				).sort(createCompareFunction<TaskListItem>(sort.property as keyof TaskListItem, sort.direction));
			},
			setOpenTaskList(payload: SetListPayload): void {
				this.taskListOpenTotalCount = payload.totalCount;
				this.taskListOpenSliced.set(payload.page, payload.payload);

				const sort = this.sortFor(ActivityListTypeEnum.OPEN);
				this.taskListOpen = dedupByAggregateId(
					[...this.taskListOpenSliced.values()].flatMap((entry) => entry)
				).sort(createCompareFunction<TaskListItem>(sort.property as keyof TaskListItem, sort.direction));
			},
			setPagination(payload: SetPaginationPayload): void {
				this.pagination.set(payload.activityListType, payload.pagination);
			},
			setPaginationAction(payload: SetPaginationPayload): void {
				this.setPagination(payload);
			},
			setPaginationPage(payload: SetPaginationPagePayload): void {
				const pagination = this.paginationFor(payload.activityListType);
				pagination.page = payload.page;
				this.pagination.set(payload.activityListType, pagination);
			},
			setPaginationPageAction(payload: SetPaginationPagePayload): void {
				this.setPaginationPage(payload);
			},
			setSelectedSharedQuery(payload: string): void {
				this.selectedSharedQueryId = payload || '';
			},
			setSelectedSharedQueryAction(payload: SetSelectedSharedQueryPayload): void {
				this.setSelectedSharedQuery(payload.selectedSharedQuery ?? '');
			},
			setSort(payload: SetSortPayload): void {
				this.sort.set(payload.activityListType, payload.sort);
			},
			setSortAction(payload: SetSortPayload): void {
				this.setSort(payload);
				if (this.$id === 'taskList') {
					localStorage.setItem(TASK_LIST_GENERIC_ACTIVITY_LIST_SORT, JSON.stringify(this.sort.toJSON()));
				}
			},
			setTheStateForAggregateIds(aggregateIds: string[], status: StatusEnum): void {
				let openCountDelta = 0;
				aggregateIds.forEach((aggregateId) => {
					const known =
						this.taskListOpen.find((item) => item.aggregateId === aggregateId) ??
						this.taskListAll.find((item) => item.aggregateId === aggregateId) ??
						this.taskListMine.find((item) => item.aggregateId === aggregateId);
					if (!known) {
						return;
					}
					const wasReady = known.statusButton.status === StatusEnum.READY;
					const willBeReady = status === StatusEnum.READY;
					if (!wasReady && willBeReady) {
						openCountDelta -= 1;
					} else if (wasReady && !willBeReady) {
						openCountDelta += 1;
					}
				});
				if (openCountDelta !== 0) {
					this.taskListOpenTotalCount = Math.max(0, this.taskListOpenTotalCount + openCountDelta);
				}

				const setPagedList = <T>(
					entries: T[],
					totalCount: number,
					applyPage: (payload: { page: number; payload: T[]; totalCount: number }) => void
				): void => {
					if (entries.length === 0) {
						return;
					}

					const paginator = new Paginator(entries, DEFAULT_ROWS_PER_PAGE, 1);
					while (paginator.current <= paginator.total) {
						applyPage({
							page: paginator.current,
							payload: paginator.page(paginator.current) as T[],
							totalCount: totalCount,
						});
						paginator.next();
					}
				};

				setPagedList<TaskListItem>(
					setTheStateForTheAggregateIds(
						this.taskListOpen as never[],
						aggregateIds,
						status
					) as unknown as TaskListItem[],
					this.taskListOpenTotalCount,
					(payload) => this.setOpenTaskList(payload)
				);
				setPagedList<TaskListItem>(
					setTheStateForTheAggregateIds(
						this.taskListAll as never[],
						aggregateIds,
						status
					) as unknown as TaskListItem[],
					this.taskListAllTotalCount,
					(payload) => this.setAllTaskList(payload)
				);
				setPagedList<TaskListItem>(
					setTheStateForTheAggregateIds(
						this.taskListMine as never[],
						aggregateIds,
						status
					) as unknown as TaskListItem[],
					this.taskListMineTotalCount,
					(payload) => this.setMineTaskList(payload)
				);
			},
			setTheTaskStateToReady(payload: SetListStatePayload): void {
				this.setTheStateForAggregateIds(payload.aggregateIds, StatusEnum.READY);
			},
			setTheTaskStateToSeen(payload: SetListStatePayload): void {
				this.setTheStateForAggregateIds(payload.aggregateIds, StatusEnum.SEEN);
			},
			setTheTaskStateToUnseen(payload: SetListStatePayload): void {
				this.setTheStateForAggregateIds(payload.aggregateIds, StatusEnum.UNSEEN);
			},
			setToday(payload: string): void {
				this.today = payload || '';
			},
			silentSetAllTaskList(): void {
				this.taskListAllTotalCount = 0;
				this.taskListAll = [];
				this.taskListAllSliced.clear();
				this.pagination.set(ActivityListTypeEnum.ALL, createDefaultPaginationEntry());
			},
			silentSetMineTaskList(): void {
				this.taskListMineTotalCount = 0;
				this.taskListMine = [];
				this.taskListMineSliced.clear();
				this.pagination.set(ActivityListTypeEnum.MINE, createDefaultPaginationEntry());
			},
			silentSetOpenTaskList(): void {
				this.taskListOpenTotalCount = 0;
				this.taskListOpen = [];
				this.taskListOpenSliced.clear();
				this.pagination.set(ActivityListTypeEnum.OPEN, createDefaultPaginationEntry());
			},
		},
		getters: {
			paginationFor(state): (activityListType: ActivityListTypeEnum) => GenericActivityListPagination {
				return (activityListType: ActivityListTypeEnum): GenericActivityListPagination =>
					state.pagination.get(activityListType) ?? createDefaultPaginationEntry();
			},
			sortFor(state): (activityListType: ActivityListTypeEnum) => GenericActivityListSort {
				return (activityListType: ActivityListTypeEnum): GenericActivityListSort =>
					state.sort.get(activityListType) ?? { ...DEFAULT_LIST_SORT };
			},
		},
		state: createTaskListState,
	});

export const useTaskListStore = defineTaskListStore('taskList');

const taskListInstanceDefinitions = new Map<string, ReturnType<typeof defineTaskListStore>>();

export const createTaskListStore = (instanceId: string = DEFAULT_LIST_INSTANCE_ID) => {
	if (isDefaultInstance(instanceId)) {
		return useTaskListStore();
	}

	const storeId = deriveListStoreId('taskList', instanceId);
	let definition = taskListInstanceDefinitions.get(storeId);

	if (definition === undefined) {
		definition = defineTaskListStore(storeId);
		taskListInstanceDefinitions.set(storeId, definition);
	}

	return definition();
};

export * from './interfaces';
export * from './types';
